<!-- UnifiedFinancialTable.vue - Updated with Age Columns -->
<template>
  <div class="unified-table-wrapper">
    <!-- Breadcrumb Navigation -->
    <div class="breadcrumb-nav" v-if="currentView !== 'summary'">
      <button @click="navigateToSummary" class="breadcrumb-btn">
        Summary
      </button>
      <span class="breadcrumb-separator">/</span>
      <span class="breadcrumb-current">{{ currentViewTitle }}</span>
    </div>

    <!-- Table Controls -->
    <div class="table-controls">
      <button 
        v-if="currentView !== 'summary'"
        @click="navigateToSummary" 
        class="back-btn"
      >
        ← Back to Summary
      </button>
    </div>

    <!-- Summary View -->
    <div v-if="currentView === 'summary'" class="table-container">
      <table class="financial-table">
        <thead>
          <tr>
            <th class="year-header">Year</th>
            <!-- Age Columns -->
            <th v-if="showPrimaryAge" class="age-header">Age</th>
            <th v-if="showSpouseAge" class="age-header">Spouse Age</th>
            <!-- Cash Flow Section (moved first) -->
            <th class="clickable-header income-header" @click="navigateToIncome">
              Total Income
            </th>
            <th class="clickable-header expenses-header" @click="navigateToExpenses">
              Total Expenses
            </th>
            <th class="summary-header savings-header">Net Savings</th>
            <!-- Portfolio Section -->
            <th class="summary-header portfolio-header">Portfolio Value</th>
            <th class="summary-header withdrawals-header">Withdrawals</th>
            <th class="summary-header shortfall-header">Shortfall</th>
            <!-- Net Worth Section (moved last) -->
            <th class="clickable-header assets-header" @click="navigateToAssets">
              Total Assets
            </th>
            <th class="clickable-header liabilities-header" @click="navigateToLiabilities" v-if="hasLiabilities">
              Total Liabilities  
            </th>
            <th class="summary-header networth-header">Net Worth</th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="row in yearRows" :key="row.year" class="data-row">
            <td class="year-cell">{{ row.year }}</td>
            <!-- Age Columns -->
            <td v-if="showPrimaryAge" class="age-cell">{{ getPrimaryAge(row.year) }}</td>
            <td v-if="showSpouseAge" class="age-cell">{{ getSpouseAge(row.year) }}</td>
            <!-- Cash Flow Section (reordered) -->
            <td class="summary-cell income-cell clickable-cell" @click="navigateToIncome">
              {{ formatCurrency(row.totalIncome || 0) }}
            </td>
            <td class="summary-cell expenses-cell clickable-cell" @click="navigateToExpenses">
              {{ formatCurrency(row.totalExpenses || 0) }}
            </td>
            <td class="summary-cell savings-cell" :class="getCashFlowClass(row.savings)">
              {{ formatCurrency(row.savings || 0) }}
            </td>
            <!-- Portfolio Section -->
            <td class="summary-cell portfolio-cell">
              {{ formatCurrency(row.portfolioValue || 0) }}
            </td>
            <td class="summary-cell withdrawals-cell">
              {{ formatCurrency(row.withdrawals || 0) }}
            </td>
            <td class="summary-cell shortfall-cell" :class="{ 'has-shortfall': row.shortfall > 0 }">
              {{ formatCurrency(row.shortfall || 0) }}
            </td>
            <!-- Net Worth Section (moved last) -->
            <td class="summary-cell assets-cell clickable-cell" @click="navigateToAssets">
              {{ formatCurrency(row.totalAssets || 0) }}
            </td>
            <td class="summary-cell liabilities-cell clickable-cell" @click="navigateToLiabilities" v-if="hasLiabilities">
              {{ formatCurrency(row.totalLiabilities || 0) }}
            </td>
            <td class="summary-cell networth-cell" :class="{ negative: row.netWorth < 0 }">
              {{ formatCurrency(row.netWorth || 0) }}
            </td>
          </tr>
        </tbody>
      </table>
    </div>

    <!-- Assets Detail View -->
    <div v-else-if="currentView === 'assets'" class="table-container">
      <table class="financial-table">
        <thead>
          <tr>
            <th class="year-header">Year</th>
            <th v-if="showPrimaryAge" class="age-header">Age</th>
            <th v-if="showSpouseAge" class="age-header">Spouse Age</th>
            <th v-for="(item, index) in assetItems" :key="`asset-${index}-${item.name}`" class="asset-header">
              {{ item.displayName || item.name }}
            </th>
            <th class="total-header">Total Assets</th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="row in yearRows" :key="row.year" class="data-row">
            <td class="year-cell">{{ row.year }}</td>
            <td v-if="showPrimaryAge" class="age-cell">{{ getPrimaryAge(row.year) }}</td>
            <td v-if="showSpouseAge" class="age-cell">{{ getSpouseAge(row.year) }}</td>
            <td v-for="(item, index) in assetItems" :key="`asset-${index}-${item.name}`" class="detail-cell asset-cell">
              {{ formatCurrency(Math.abs(row[`nw_${item.id}`] || 0)) }}
            </td>
            <td class="total-cell">
              {{ formatCurrency(row.totalAssets || 0) }}
            </td>
          </tr>
        </tbody>
      </table>
    </div>

    <!-- Liabilities Detail View -->
    <div v-else-if="currentView === 'liabilities'" class="table-container">
      <table class="financial-table">
        <thead>
          <tr>
            <th class="year-header">Year</th>
            <th v-if="showPrimaryAge" class="age-header">Age</th>
            <th v-if="showSpouseAge" class="age-header">Spouse Age</th>
            <th v-for="(item, index) in liabilityItems" :key="`liability-${index}-${item.name}`" class="liability-header">
              {{ item.displayName || item.name }}
            </th>
            <th class="total-header">Total Liabilities</th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="row in yearRows" :key="row.year" class="data-row">
            <td class="year-cell">{{ row.year }}</td>
            <td v-if="showPrimaryAge" class="age-cell">{{ getPrimaryAge(row.year) }}</td>
            <td v-if="showSpouseAge" class="age-cell">{{ getSpouseAge(row.year) }}</td>
            <td v-for="(item, index) in liabilityItems" :key="`liability-${index}-${item.name}`" class="detail-cell liability-cell">
              {{ formatCurrency(Math.abs(row[`nw_${item.id}`] || 0)) }}
            </td>
            <td class="total-cell">
              {{ formatCurrency(row.totalLiabilities || 0) }}
            </td>
          </tr>
        </tbody>
      </table>
    </div>

    <!-- Income Detail View -->
    <div v-else-if="currentView === 'income'" class="table-container">
      <table class="financial-table">
        <thead>
          <tr>
            <th class="year-header">Year</th>
            <th v-if="showPrimaryAge" class="age-header">Age</th>
            <th v-if="showSpouseAge" class="age-header">Spouse Age</th>
            <th v-for="(item, index) in incomeItems" 
                :key="`income-${item.originalIndex || index}-${item.name}`" 
                class="income-header">
              {{ item.displayName || item.name }}
            </th>
            <th class="total-header">Total Income</th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="row in yearRows" :key="row.year" class="data-row">
            <td class="year-cell">{{ row.year }}</td>
            <td v-if="showPrimaryAge" class="age-cell">{{ getPrimaryAge(row.year) }}</td>
            <td v-if="showSpouseAge" class="age-cell">{{ getSpouseAge(row.year) }}</td>
            <td v-for="(item, index) in incomeItems" 
                :key="`income-cell-${item.originalIndex || index}-${row.year}`" 
                class="detail-cell income-cell">
              {{ formatCurrency(Math.abs(row[`cf_${item.id}`] || 0)) }}
            </td>
            <td class="total-cell">
              {{ formatCurrency(row.totalIncome || 0) }}
            </td>
          </tr>
        </tbody>
      </table>
    </div>

    <!-- Expenses Detail View -->
    <div v-else-if="currentView === 'expenses'" class="table-container">
      <table class="financial-table">
        <thead>
          <tr>
            <th class="year-header">Year</th>
            <th v-if="showPrimaryAge" class="age-header">Age</th>
            <th v-if="showSpouseAge" class="age-header">Spouse Age</th>
            <!-- Use originalIndex which comes from your existing processItemsWithDuplicates -->
            <th v-for="(item, index) in expenseItems" 
                :key="`expense-${item.originalIndex || index}-${item.name}`" 
                class="expense-header">
              {{ item.displayName || item.name }}
            </th>
            <th class="total-header">Total Expenses</th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="row in yearRows" :key="row.year" class="data-row">
            <td class="year-cell">{{ row.year }}</td>
            <td v-if="showPrimaryAge" class="age-cell">{{ getPrimaryAge(row.year) }}</td>
            <td v-if="showSpouseAge" class="age-cell">{{ getSpouseAge(row.year) }}</td>
            <!-- FIXED: Access data using originalIndex -->
            <td v-for="(item, index) in expenseItems" 
                :key="`expense-cell-${item.originalIndex || index}-${row.year}`" 
                class="detail-cell expense-cell">
              {{ formatCurrency(Math.abs(row[`cf_${item.id}`] || 0)) }}
            </td>
            <td class="total-cell">
              {{ formatCurrency(row.totalExpenses || 0) }}
            </td>
          </tr>
        </tbody>
      </table>
    </div>
  </div>
</template>

<script>
export default {
  name: 'UnifiedFinancialTable',
  props: {
    yearRows: {
      type: Array,
      required: true
    },
    cashFlowItems: {
      type: Array,
      required: true
    },
    netWorthItems: {
      type: Array,
      required: true
    },
    personalInfo: {
      type: Object,
      default: () => ({
        primary: {
          dateOfBirth: null,
          targetRetirementAge: null,
          lifeExpectancy: null
        },
        spouse: {
          dateOfBirth: null,
          targetRetirementAge: null,
          lifeExpectancy: null
        }
      })
    },
    formatCurrency: {
      type: Function,
      required: true
    }
  },
  data() {
    return {
      currentView: 'summary' // 'summary', 'assets', 'liabilities', 'income', 'expenses'
    };
  },
  computed: {
    incomeItems() {
      return this.processItemsWithDuplicates(
        this.cashFlowItems.filter(item => item.type === 'income')
      );
    },
    expenseItems() {
      return this.processItemsWithDuplicates(
        this.cashFlowItems.filter(item => item.type === 'expense')
      );
    },
    assetItems() {
      return this.processItemsWithDuplicates(
        this.netWorthItems.filter(item => item.type === 'asset')
      );
    },
    liabilityItems() {
      return this.processItemsWithDuplicates(
        this.netWorthItems.filter(item => item.type === 'liability')
      );
    },
    hasLiabilities() {
      return this.liabilityItems.length > 0;
    },
    showPrimaryAge() {
      return this.personalInfo?.primary?.dateOfBirth != null;
    },
    showSpouseAge() {
      return this.personalInfo?.spouse?.dateOfBirth != null;
    },
    tableTitle() {
      const titles = {
        summary: 'Financial Projection Summary',
        assets: 'Assets Breakdown',
        liabilities: 'Liabilities Breakdown', 
        income: 'Income Breakdown',
        expenses: 'Expenses Breakdown'
      };
      return titles[this.currentView] || 'Financial Projection';
    },
    currentViewTitle() {
      const titles = {
        assets: 'Assets',
        liabilities: 'Liabilities',
        income: 'Income', 
        expenses: 'Expenses'
      };
      return titles[this.currentView] || '';
    }
  },
  methods: {
    calculateAge(dateOfBirth, forYear) {
      if (!dateOfBirth) return null;
      
      const currentYear = new Date().getFullYear();
      
      // Handle different date formats
      let birthYear;
      if (typeof dateOfBirth === 'string') {
        // Try parsing various date formats
        const date = new Date(dateOfBirth);
        if (!isNaN(date.getTime())) {
          birthYear = date.getFullYear();
        } else {
          // Try extracting year from string formats like "1990", "1990-01-01", etc.
          const yearMatch = dateOfBirth.match(/\b(19|20)\d{2}\b/);
          if (yearMatch) {
            birthYear = parseInt(yearMatch[0]);
          }
        }
      } else if (typeof dateOfBirth === 'number') {
        birthYear = dateOfBirth;
      }
      
      if (!birthYear || birthYear < 1900 || birthYear > currentYear) {
        return null;
      }
      
      return forYear - birthYear;
    },
    getPrimaryAge(year) {
      const age = this.calculateAge(this.personalInfo?.primary?.dateOfBirth, year);
      return age !== null ? age : '-';
    },
    getSpouseAge(year) {
      const age = this.calculateAge(this.personalInfo?.spouse?.dateOfBirth, year);
      return age !== null ? age : '-';
    },
    processItemsWithDuplicates(items) {
      // Create a map to track duplicate names
      const nameCount = {};
      const processedItems = [];
      
      items.forEach(item => {
        const baseName = item.name;
        
        // Count occurrences of each name
        if (!nameCount[baseName]) {
          nameCount[baseName] = 0;
        }
        nameCount[baseName]++;
        
        // If there are duplicates, add a suffix
        let displayName = baseName;
        if (nameCount[baseName] > 1) {
          // You can customize this suffix based on your needs
          // For example, if items have year ranges, you could use those
          displayName = `${baseName} (${nameCount[baseName]})`;
          
          // Alternative: if items have additional properties like yearStart/yearEnd
          if (item.yearStart && item.yearEnd) {
            displayName = `${baseName} (${item.yearStart}-${item.yearEnd})`;
          }
        }
        
        processedItems.push({
          ...item,
          displayName: displayName
        });
      });
      
      return processedItems;
    },
    getCashFlowClass(value) {
      if (value > 0) {
        return 'positive-cash-flow';
      } else if (value < 0) {
        return 'negative-cash-flow';
      }
      return '';
    },
    navigateToSummary() {
      this.currentView = 'summary';
    },
    navigateToAssets() {
      this.currentView = 'assets';
    },
    navigateToLiabilities() {
      if (this.hasLiabilities) {
        this.currentView = 'liabilities';
      }
    },
    navigateToIncome() {
      this.currentView = 'income';
    },
    navigateToExpenses() {
      this.currentView = 'expenses';
    }
  }
};
</script>

